// apiClient.ts
import { AuthTimeoutError, AuthenticationError } from './errors';

const BASE_API_URL: string = import.meta.env.VITE_BASE_API_URL as string;
const AUTH_STORAGE_KEY = 'auth:loggedIn';

function clearAuthState(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  // Clear any other auth-related items in localStorage or sessionStorage if needed
}

function isUserLoggedIn(): boolean {
  return localStorage.getItem(AUTH_STORAGE_KEY) === 'true';
}

export function setIsUserLoggedIn(value: boolean): void {
  if (value) {
    localStorage.setItem(AUTH_STORAGE_KEY, 'true');
  } else {
    clearAuthState();
  }
}

let refreshingPromise: Promise<Response> | null = null;

async function refreshToken(): Promise<Response> {
  if (refreshingPromise) {
    return refreshingPromise;
  }

  refreshingPromise = fetch(`${BASE_API_URL}/auth/token/refresh/`, {
    method: 'POST',
    credentials: 'include',
  });

  try {
    const response = await refreshingPromise;
    if (!response.ok) {
      clearAuthState(); // Use our new function
      throw new AuthenticationError('Token refresh failed');
    }
    return response;
  } finally {
    refreshingPromise = null;
  }
}

function isPublicAuthEndpoint(endpoint: string): boolean {
  return (
    endpoint.startsWith('/auth/token') ||
    endpoint.startsWith('/auth/invite') ||
    endpoint.startsWith('/auth/password-reset')
  );
}

async function waitForLoginOrTimeout(timeoutMs: number): Promise<void> {
  if (isUserLoggedIn()) {
    return;
  }

  let elapsed = 0;
  const interval = 200;

  return new Promise<void>((resolve, reject) => {
    const timer = setInterval(() => {
      elapsed += interval;
      if (isUserLoggedIn()) {
        clearInterval(timer);
        resolve();
      } else if (elapsed >= timeoutMs) {
        clearInterval(timer);
        reject(new AuthTimeoutError('Timed out waiting for user to log in.'));
      }
    }, interval);
  });
}

export async function fetchWithAuth(
  endpoint: string,
  init?: RequestInit,
): Promise<Response> {
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/')
    ? endpoint
    : `/${endpoint}`;

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${BASE_API_URL}${normalizedEndpoint}`;

  // Try the request first with credentials
  let response = await fetch(url, {
    ...init,
    credentials: 'include',
  });

  // If we get 401, try to refresh token and retry
  // But don't attempt refresh for public auth endpoints - just return the response
  // so the calling code can handle the error appropriately (e.g., show "incorrect password")
  if (response.status === 401) {
    if (isPublicAuthEndpoint(endpoint)) {
      return response;
    }

    try {
      await refreshToken();
      response = await fetch(url, { ...init, credentials: 'include' });
    } catch (err) {
      // If refresh fails, check if we need to wait for login
      if (!isUserLoggedIn()) {
        try {
          await waitForLoginOrTimeout(5000);
          // Try once more after waiting for login
          response = await fetch(url, { ...init, credentials: 'include' });
        } catch (waitError) {
          throw waitError;
        }
      } else {
        throw err;
      }
    }
  }

  return response;
}

// Walk a nested DRF error body and collect all leaf error strings.
// Tracks the most-recent object key seen so messages include field context
// (e.g. "localCostMultiplier: Ensure that there are no more than 2 digits…").
function collectDrfErrors(obj: unknown, lastKey?: string): string[] {
  if (typeof obj === 'string') {
    return [lastKey ? `${lastKey}: ${obj}` : obj];
  }
  if (Array.isArray(obj)) {
    return obj.flatMap((item) =>
      typeof item === 'string'
        ? [lastKey ? `${lastKey}: ${item}` : item]
        : collectDrfErrors(item, lastKey),
    );
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.entries(obj as Record<string, unknown>).flatMap(([k, v]) =>
      collectDrfErrors(v, k),
    );
  }
  return [];
}

const METHODS_WITH_BODY = ['POST', 'PUT', 'PATCH'] as const;
type MethodWithBody = (typeof METHODS_WITH_BODY)[number];

export async function apiFetch<T = unknown>(
  endpoint: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetchWithAuth(endpoint, {
    ...init,
    headers: {
      ...(init?.method &&
        METHODS_WITH_BODY.includes(init.method as MethodWithBody) && {
          'Content-Type': 'application/json',
        }),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();

    // Try to parse as JSON to extract user-friendly error message
    let errorData: any = null;
    try {
      errorData = JSON.parse(errorText);
    } catch (parseError) {
      // If parsing fails, use the raw error text
      if (errorText && errorText.trim() && !errorText.startsWith('<!DOCTYPE')) {
        throw new Error(errorText);
      }
      throw new Error(`Request failed with status ${response.status}`);
    }

    // Successfully parsed JSON, now extract meaningful error message
    if (errorData && typeof errorData === 'object') {
      if (typeof errorData.detail === 'string') {
        throw new Error(errorData.detail);
      }
      const messages = collectDrfErrors(errorData);
      if (messages.length > 0) throw new Error(messages.join('\n'));
    }

    // Fallback to generic message
    throw new Error('An error occurred. Please try again.');
  }
  // Handle 204 No Content responses without trying to parse JSON
  if (response.status === 204) {
    return null as T;
  }
  return (await response.json()) as T;
}
